import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { workReports as api, emailReport, reportPdfUrl, reportPdfDownloadUrl, apiError, API } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Loading, Card } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Mail, Pencil, Loader2 } from "lucide-react";

const RPT_STATUS = { draft: "draft", in_progress: "pending", completed: "accepted", sent: "sent_status" };

export default function ReportView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => { api.get(id).then(setR).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!r) return <Loading />;

  const mail = async () => { setEmailing(true); try { const res = await emailReport(id); toast.success(res.message); setR({ ...r, status: "sent" }); } catch (e) { toast.error(apiError(e)); } finally { setEmailing(false); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <button onClick={() => nav("/work-reports")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B]" data-testid="report-view-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => nav(`/work-reports/${id}/edit`)} data-testid="edit-report-view"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
          <a href={reportPdfDownloadUrl(id)} download data-testid="download-report-pdf"><Button variant="outline"><FileDown className="w-4 h-4 mr-1.5" /> Download PDF</Button></a>
          <ShareButton docType="report" docId={id} />
          <Button onClick={mail} disabled={emailing} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="email-report-view">{emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Email Report</>}</Button>
        </div>
      </div>

      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-2xl font-bold text-[#0B132B]">{r.number}</div>
            <div className="text-sm text-slate-500">{r.title} · {r.customer?.name} · {fmtDate(r.assessment_date)}</div>
          </div>
          <StatusBadge status={RPT_STATUS[r.status] || "draft"} testId="report-view-status" />
        </div>
      </Card>

      <Card className="p-2 md:p-4" data-testid="report-pdf-preview">
        <div className="text-xs text-slate-500 px-2 py-1">PDF Preview</div>
        <iframe title="Report PDF" src={reportPdfUrl(id)} className="w-full rounded-lg border border-slate-200" style={{ height: "80vh" }} />
      </Card>
    </div>
  );
}
